import React, { useState, useEffect } from 'react';
import { Image, Filter, X, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { galleryData } from '../data/galleryData';

const allTags = [...new Set(galleryData.flatMap(item => item.tags))];
const yearTags = allTags.filter(tag => /^\d{4}$/.test(tag)).sort().reverse();

const Gallery = () => {
  const [selectedYearTag, setSelectedYearTag] = useState(yearTags[0] || '2024');
  const [selectedContentTags, setSelectedContentTags] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  
  // Get content tags available for the selected year
  const availableContentTags = [...new Set(
    galleryData
      .filter(item => item.tags.includes(selectedYearTag))
      .flatMap(item => item.tags.filter(tag => !/^\d{4}$/.test(tag)))
  )].sort();
  
  // Create dynamic content tag categories based on available tags
  const contentTagCategories = [
    { id: 'all', name: 'Toutes les photos', tags: [] },
    // Group tags by common themes when possible
    ...(availableContentTags.some(tag => ['techniques', 'projection', 'immobilisation'].includes(tag)) 
      ? [{ id: 'techniques', name: 'Techniques', tags: availableContentTags.filter(tag => ['techniques', 'projection', 'immobilisation'].includes(tag)) }] 
      : []),
    ...(availableContentTags.some(tag => ['evenement', 'presentation', 'demonstration'].includes(tag)) 
      ? [{ id: 'evenement', name: 'Événements', tags: availableContentTags.filter(tag => ['evenement', 'presentation', 'demonstration'].includes(tag)) }] 
      : []),
    ...(availableContentTags.some(tag => ['entrainement', 'cours', 'decouverte'].includes(tag)) 
      ? [{ id: 'entrainement', name: 'Entraînement', tags: availableContentTags.filter(tag => ['entrainement', 'cours', 'decouverte'].includes(tag)) }] 
      : []),
    ...(availableContentTags.includes('enfants') 
      ? [{ id: 'enfants', name: 'Cours enfants', tags: ['enfants'] }] 
      : []),
    // Add individual tags that don't fit into groups
    ...availableContentTags
      .filter(tag => !['techniques', 'projection', 'immobilisation', 'evenement', 'presentation', 'demonstration', 'entrainement', 'cours', 'decouverte', 'enfants'].includes(tag))
      .map(tag => ({ id: tag, name: tag.charAt(0).toUpperCase() + tag.slice(1), tags: [tag] }))
  ];

  // Filter by year tag first, then by content tags
  const filteredGallery = galleryData
    .filter(item => item.tags.includes(selectedYearTag))
    .filter(item => {
      if (selectedContentTags.length === 0) return true;
      return selectedContentTags.some(tag => item.tags.includes(tag));
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredGallery.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedGallery = filteredGallery.slice(startIndex, startIndex + itemsPerPage);

  // Reset pagination when filters change
  const handleYearTagChange = (yearTag) => {
    setSelectedYearTag(yearTag);
    setSelectedContentTags([]); // Reset content filter when year changes
    setCurrentPage(1);
  };

  const handleContentTagChange = (categoryId) => {
    const category = contentTagCategories.find(cat => cat.id === categoryId);
    if (category) {
      setSelectedContentTags(category.tags);
      setCurrentPage(1);
    }
  };

  const openLightbox = (image, index) => {
    setLightboxImage(image);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const navigateLightbox = (direction) => {
    if (!lightboxImage) return;
    
    const newIndex = direction === 'prev' 
      ? (lightboxIndex - 1 + filteredGallery.length) % filteredGallery.length
      : (lightboxIndex + 1) % filteredGallery.length;
    
    setLightboxIndex(newIndex);
    setLightboxImage(filteredGallery[newIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!lightboxImage) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          navigateLightbox('prev');
          break;
        case 'ArrowRight':
          event.preventDefault();
          navigateLightbox('next');
          break;
        case 'Escape':
          event.preventDefault();
          closeLightbox();
          break;
        default:
          break;
      }
    };

    if (lightboxImage) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxImage, lightboxIndex, filteredGallery]);

  return (
    <section id="gallery" className="section-padding section-dark">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-4">
          <h2 className="heading-1 mb-4" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Galerie</h2>
          <p className="body-large max-w-2xl mx-auto" style={{ color: 'var(--text-on-dark)' }}>
            Découvrez nos dojos et nos pratiquants.
          </p>
        </div>

        {/* Year Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'var(--text-on-dark)' }}>Année :</span>
          </div>
          {yearTags.map(yearTag => (
            <button
              key={yearTag}
              onClick={() => handleYearTagChange(yearTag)}
              className={`px-3 py-1 text-sm rounded-full border transition-all ${
                selectedYearTag === yearTag 
                  ? 'btn-primary' 
                  : 'border-opacity-50 hover:border-opacity-100'
              }`}
              style={{
                ...(selectedYearTag !== yearTag && {
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary-dark)',
                  borderColor: 'var(--border-medium)'
                })
              }}
            >
              {yearTag}
            </button>
          ))}
        </div>

        {/* Content Tag Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={16} style={{ color: 'var(--text-secondary-dark)' }} />
            <span className="text-sm" style={{ color: 'var(--text-secondary-dark)' }}>Contenu :</span>
          </div>
          {contentTagCategories.map(category => (
            <button
              key={category.id}
              onClick={() => handleContentTagChange(category.id)}
              className={`px-3 py-1 text-sm rounded border transition-all ${
                (category.id === 'all' && selectedContentTags.length === 0) ||
                (category.tags.length > 0 && category.tags.every(tag => selectedContentTags.includes(tag)))
                  ? 'btn-primary' 
                  : 'border-opacity-50 hover:border-opacity-100'
              }`}
              style={{
                ...((category.id !== 'all' || selectedContentTags.length > 0) &&
                    !(category.tags.length > 0 && category.tags.every(tag => selectedContentTags.includes(tag))) && {
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary-dark)',
                  borderColor: 'var(--border-medium)'
                })
              }}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="gallery-grid">
          {paginatedGallery.map((item, paginatedIndex) => {
            const actualIndex = filteredGallery.findIndex(galleryItem => galleryItem.id === item.id);
            return (
              <div 
                key={item.id} 
                className="gallery-item cursor-pointer group rounded-lg overflow-hidden"
                onClick={() => openLightbox(item, actualIndex)}
                style={{ backgroundColor: 'var(--bg-card-dark)' }}
              >
              {item.thumbnail || item.src ? (
                <div className="relative h-full">
                  <img 
                    src={item.thumbnail || item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300"></div>
                  
                  {/* Video Play Icon */}
                  {item.type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-black bg-opacity-50 rounded-full p-4 group-hover:bg-opacity-70 transition-all duration-300">
                        <Play size={32} className="text-white ml-1" fill="currentColor" />
                      </div>
                    </div>
                  )}
                  
                  <div className="absolute bottom-2 right-2">
                    <div className="flex flex-wrap gap-1">
                      {item.tags.filter(tag => !/^\d{4}$/.test(tag)).slice(0, 2).map((tag, tagIndex) => (
                        <span 
                          key={tagIndex}
                          className="px-2 py-1 rounded-full text-xs"
                          style={{ 
                            backgroundColor: 'var(--brand-primary)', 
                            color: 'white' 
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center">
                  <Image 
                    size={48} 
                    style={{ color: 'var(--text-secondary-dark)' }} 
                    className="mx-auto mb-4 group-hover:scale-110 transition-transform"
                  />
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--brand-primary)', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                    Photo {item.id}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-on-dark)' }}>{item.alt}</p>
                  <div className="flex flex-wrap gap-1 mt-3 justify-center">
                    {item.tags.filter(tag => !/^\d{4}$/.test(tag)).slice(0, 3).map((tag, tagIndex) => (
                      <span 
                        key={tagIndex}
                        className="px-2 py-1 rounded-full text-xs"
                        style={{ 
                          backgroundColor: 'var(--brand-primary)', 
                          color: 'white' 
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>

        {filteredGallery.length === 0 && (
          <div className="text-center py-16">
            <Image size={48} style={{ color: 'var(--text-secondary-dark)' }} className="mx-auto mb-4" />
            <p className="body-medium" style={{ color: 'var(--text-on-dark)' }}>Aucun contenu trouvé pour cette sélection.</p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center mt-8 gap-3">
            {/* Results info */}
            <p className="body-small" style={{ color: 'var(--text-secondary-dark)' }}>
              Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredGallery.length)} sur {filteredGallery.length} éléments
            </p>
            
            {/* Pagination buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-on-dark)',
                  borderColor: 'var(--border-medium)'
                }}
              >
                <ChevronLeft size={20} />
              </button>
              
              {/* Page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 rounded border transition-all ${
                    currentPage === page 
                      ? 'btn-primary' 
                      : 'border-opacity-50 hover:border-opacity-100'
                  }`}
                  style={{
                    ...(currentPage !== page && {
                      backgroundColor: 'transparent',
                      color: 'var(--text-on-dark)',
                      borderColor: 'var(--border-medium)'
                    })
                  }}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text-on-dark)',
                  borderColor: 'var(--border-medium)'
                }}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {lightboxImage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
            onClick={closeLightbox}
          >
            <div className="relative max-w-4xl w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  closeLightbox();
                }}
                className="absolute top-4 right-4 p-2 rounded-full z-10"
                style={{ backgroundColor: 'var(--bg-light)', color: 'var(--brand-primary)' }}
              >
                <X size={24} />
              </button>
              
              {/* Navigation buttons */}
              {filteredGallery.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateLightbox('prev');
                    }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full z-10 transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white' }}
                  >
                    <ChevronLeft size={28} />
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateLightbox('next');
                    }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full z-10 transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white' }}
                  >
                    <ChevronRight size={28} />
                  </button>
                </>
              )}
              
              {lightboxImage.src ? (
                <div className="text-center">
                  {lightboxImage.type === 'video' ? (
                    <video 
                      src={lightboxImage.src}
                      controls
                      className="max-w-full max-h-[70vh] object-contain rounded-lg mx-auto mb-4"
                      onClick={(e) => e.stopPropagation()}
                      style={{ backgroundColor: 'black' }}
                    >
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  ) : (
                    <img 
                      src={lightboxImage.src}
                      alt={lightboxImage.alt}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg mx-auto mb-4"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--bg-card-dark)' }}>
                    {filteredGallery.length > 1 && (
                      <p className="text-sm mb-2" style={{ color: 'var(--text-secondary-dark)' }}>
                        {lightboxIndex + 1} / {filteredGallery.length}
                      </p>
                    )}
                    <p className="body-medium mb-3 text-white">
                      {lightboxImage.alt}
                    </p>
                    {lightboxImage.caption && (
                      <p className="text-sm mb-3 italic" style={{ color: 'var(--text-secondary-dark)' }}>
                        {lightboxImage.caption}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 justify-center">
                      {lightboxImage.tags.map((tag, tagIndex) => (
                        <span 
                          key={tagIndex}
                          className="px-3 py-1 rounded-full text-sm"
                          style={{ 
                            backgroundColor: /^\d{4}$/.test(tag) ? 'var(--brand-secondary)' : 'var(--brand-primary)', 
                            color: 'white' 
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  className="w-full h-96 border-2 border-dashed rounded-lg flex items-center justify-center"
                  style={{ 
                    borderColor: 'var(--border-medium)',
                    backgroundColor: 'var(--bg-light)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="text-center">
                    <Image size={64} style={{ color: 'var(--brand-primary)' }} className="mx-auto mb-4" />
                    <h3 className="heading-3 mb-2" style={{ color: 'var(--brand-primary)', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>
                      Photo {lightboxImage.id}
                    </h3>
                    <p className="body-medium" style={{ color: 'var(--text-on-light)' }}>{lightboxImage.alt}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Call to Action */}
	  {/*<div className="text-center mt-10">
          <h3 className="heading-3 mb-4">Ready to Join Our Community?</h3>
          <p className="body-medium mb-8" style={{ color: 'var(--text-on-dark)' }}>
            Book your free trial class today and experience what makes our dojo special.
          </p>
          <button className="btn-primary">
            Book Free Trial Class
          </button>
        </div>*/}
      </div>
    </section>
  );
};

export default Gallery;
